import { AXNUI_CONTENT_PROPERTY_TYPE_ID, AXNUI_LAYOUT_PROPERTY_TYPE_ID, AXNUI_THEME_PROPERTY_TYPE_ID, DCUI_CONTENT_PROPERTY_TYPE_ID, DCUI_THEME_PROPERTY_TYPE_ID, EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";
import { OIDC_CREATEACCOUNT_SERVICE_TYPE } from "@constants/verification-service-types";
import { PolicyConfigDecisionAttributeFieldResultTypes } from "@enums/policy-config-decision-attribute-field-result-type";
import { DEFAULT_AXNUI_CONTENT, DEFAULT_AXNUI_LAYOUT, DEFAULT_AXNUI_THEME, DEFAULT_DCUI_CONTENT, DEFAULT_DCUI_THEME } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes, PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, DCUIContent, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { AppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { FormattedIdwProfileProperty, IdwProfile, IdwProfileProperty } from "@idw-types/data/idw-profile";
import {
    AxnAttributeTypeField,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnIdentityProvider,
    AxnRelyingParty,
    AxnServiceOfferingDataSource
} from "@idw-types/data/reference-data/axn";
import {
    ApAssertion,
    Assertion,
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    DeployedPolicyComponent,
    FallbackServiceMapping,
    RpServiceAttributeProperty,
    RpServiceAttributePropertyMap,
    ServiceOffering
} from "@idw-types/data/reference-data/generic";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationIdentityProvider,
    FormattedRpServiceConfigurationPolicy,
    FormattedRpServiceConfigurationPolicyConfig,
    FormattedRpServiceConfigurationProperty,
    RpServiceConfiguration,
    RpServiceConfigurationAttribute,
    RpServiceConfigurationGeneralSettings,
    RpServiceConfigurationIdentityProvider,
    RpServiceConfigurationPolicyConfig,
    RpServiceConfigurationPolicyConfigDecision,
    RpServiceConfigurationPolicyConfigDecisionCondition,
    RpServiceConfigurationProperty,
    RpServiceConfigurationServiceOffering,
    RpServiceConfigurationServiceOfferingAssertion
} from "@idw-types/data/rp-service-configuration";
import { FormattedRpServiceConfigurationScoreModifier } from "@idw-types/data/rp-service-configuration/rp-service-configuration-score";
import { getPropertyValuesPassedToService, getPropertyValuesPassedToServiceWithDefault } from "@utils/applied-profile-util";
import { extractAvailablePropertiesFromCategories } from "@utils/available-properties-util";
import { getAxnuiContent, performUnmergeForContentOuter, performUnmergeForForMultiLanguageContentOuter } from "@utils/content-util";
import { getAssertionScoreModifiers, isAssertionScoreEnabled, isTrustScoreEnabled } from "@utils/scores/score-util";
import { formatAppliedProfiles } from "../applied-profiles/applied-profiles-formatter";
import { formatRpServiceConfigurationAttributes } from "./rp-service-configuration-attribute-formatter";
import { formatRpServiceConfigurationPolicyConfig } from "./rp-service-configuration-policy-config-formatter";
import { formatRpServiceConfigurationProperties } from "./rp-service-configuration-property-formatter";
import { formatScoreModifiers } from "./rp-service-configuration-score-formatter";
import { formatRpServiceConfigurationServiceOfferings } from "./rp-service-configuration-service-offering-formatter";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

export const formatRpServiceConfigurations = (
    rpServiceConfigurations: Array<RpServiceConfiguration>,
    identityProviders: Array<AxnIdentityProvider>,
    relyingParties: Array<AxnRelyingParty>,
    availablePropertyCategories: Array<AxnAvailablePropertyCategory>,
    serviceOfferings: Array<ServiceOffering>,
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>,
    fallbackServiceMappings: Array<FallbackServiceMapping>,
    attributeTypes: Array<AttributeType>,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>,
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>,
    assertionStatusMaps: Array<ApAssertionStatusMap>,
    assertionScoreServiceOfferingIDs: Array<string>,
    trustScoreServiceOfferingIDs: Array<string>,
    axnCompanies: Array<AxnCompany>
): Array<FormattedRpServiceConfiguration> => {
    return rpServiceConfigurations.map(config => formatRpServiceConfiguration(
        config,
        identityProviders,
        relyingParties,
        availablePropertyCategories,
        serviceOfferings,
        serviceOfferingDataSources,
        fallbackServiceMappings,
        attributeTypes,
        attributeLabels,
        attributeClassifications,
        apAssertions,
        assertions,
        deployedPolicyComponents,
        axnAttributeTypeFields,
        rpServiceAttributeProperties,
        rpServiceAttributePropertyMaps,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        trustScoreServiceOfferingIDs,
        axnCompanies
    ));
};

export const formatRpServiceConfiguration = (
    rpServiceConfiguration: RpServiceConfiguration,
    identityProviders: Array<AxnIdentityProvider>,
    relyingParties: Array<AxnRelyingParty>,
    availablePropertyCategories: Array<AxnAvailablePropertyCategory>,
    serviceOfferings: Array<ServiceOffering>,
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>,
    fallbackServiceMappings: Array<FallbackServiceMapping>,
    attributeTypes: Array<AttributeType>,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>,
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>,
    assertionStatusMaps: Array<ApAssertionStatusMap>,
    assertionScoreServiceOfferingIDs: Array<string>,
    trustScoreServiceOfferingIDs: Array<string>,
    axnCompanies: Array<AxnCompany>
): FormattedRpServiceConfiguration => {
    const idps: Array<FormattedRpServiceConfigurationIdentityProvider> = identityProviders.map(idp => {
        const foundIdp = rpServiceConfiguration.idps.find(selectedIdp => selectedIdp.identityProviderID === idp.id);
        return {
            identityProvider: idp,
            id: foundIdp?.id,
            rpServiceID: foundIdp?.rpServiceID,
            addedToService: !!foundIdp
        }
    });

    const relyingParty = relyingParties.find(rp => rp.id === rpServiceConfiguration.relyingPartyID);
    const organization = axnCompanies.find(axnCompany => axnCompany.id === `${relyingParty?.companyID}`) || null;
  
    let generalSettings: RpServiceConfigurationGeneralSettings = {
        enableGatewayUI: true
    };
    if (rpServiceConfiguration.generalSettings) {
        generalSettings = rpServiceConfiguration.generalSettings;
    }

    const availableProperties = extractAvailablePropertiesFromCategories(availablePropertyCategories);

    const properties: Array<FormattedRpServiceConfigurationProperty> = formatRpServiceConfigurationProperties(
        rpServiceConfiguration.properties,
        availableProperties
    );

    const formattedServiceOfferings = formatRpServiceConfigurationServiceOfferings(
        rpServiceConfiguration.serviceOfferings || [],
        serviceOfferings,
        apAssertions,
        assertions,
        serviceOfferingDataSources,
        fallbackServiceMappings
    );

    const autoSubmitFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "autoSubmitFlag");
    const doNotSaveToPDSAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "doNotSaveToPDS");
    const ignorePDSDataAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "ignorePDSData");
    const isAsyncAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "isAsync");
    const optionalFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "optionalFlag");
    const placeholderFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "placeholderFlag");
    const requiredForSelfServiceVerificationAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "requiredForSelfServiceVerification");
    const userEditableFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "userEditableFlag");
    const userVisibleFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "userVisibleFlag");
    const customToolTipAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "customToolTip");
    const displayFullValueAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "displayFullValue");

    const axnuiContent: AXNUIContent = getAxnuiContent(properties);

    const formattedAttributes = formatRpServiceConfigurationAttributes(
        rpServiceConfiguration.attributes || [],
        attributeTypes,
        attributeLabels,
        attributeClassifications,
        rpServiceAttributePropertyMaps,
        autoSubmitFlagAttributeProperty,
        doNotSaveToPDSAttributeProperty,
        ignorePDSDataAttributeProperty,
        isAsyncAttributeProperty,
        optionalFlagAttributeProperty,
        placeholderFlagAttributeProperty,
        requiredForSelfServiceVerificationAttributeProperty,
        userEditableFlagAttributeProperty,
        userVisibleFlagAttributeProperty,
        customToolTipAttributeProperty,
        displayFullValueAttributeProperty,
        axnuiContent
    );

    const formattedPolicyConfig = formatRpServiceConfigurationPolicyConfig(
        rpServiceConfiguration.policyConfig,
        deployedPolicyComponents,
        axnAttributeTypeFields
    );

    const formattedPolicies: Array<FormattedRpServiceConfigurationPolicy> = (rpServiceConfiguration.policies || []).map(policy => ({
        ...policy,
        stepUpEnabled: (policy.properties || []).some(property => property.name === "stepUp.apiKey" && property.value)
    }));

    let formattedAssertionScoreModifiers: FormattedRpServiceConfigurationScoreModifier[] = [];
    let formattedTrustScoreModifiers: FormattedRpServiceConfigurationScoreModifier[] = [];

    if (isAssertionScoreEnabled(formattedServiceOfferings, assertionScoreServiceOfferingIDs)) {
        formattedAssertionScoreModifiers = formatScoreModifiers(
            rpServiceConfiguration.assertionScoreModifiers,
            formattedServiceOfferings,
            assertionStatusMaps
        );
    }

    if (isTrustScoreEnabled(formattedServiceOfferings, trustScoreServiceOfferingIDs)) {
        formattedTrustScoreModifiers = formatScoreModifiers(
            rpServiceConfiguration.trustScoreModifiers,
            formattedServiceOfferings,
            assertionStatusMaps
        );
    }

    const appliedProfiles = formatAppliedProfiles(
        (rpServiceConfiguration.appliedProfiles || []),
        axnCompanies,
        availableProperties,
        false // shouldPopulateMissingProperties
    );

    return {
        afterIDPAuthURL: rpServiceConfiguration.afterIDPAuthURL,
        apiKey: rpServiceConfiguration.apiKey,
        appliedProfiles: appliedProfiles,
        assertionScoreModifiers: formattedAssertionScoreModifiers,
        attributes: formattedAttributes,
        callBackURL: rpServiceConfiguration.callBackURL,
        customIDPDisplayURL: rpServiceConfiguration.customIDPDisplayURL,
        endpointTypeID: rpServiceConfiguration.endpointTypeID,
        errorURL: rpServiceConfiguration.errorURL,
        generalSettings: generalSettings,
        id: rpServiceConfiguration.id,
        idps: idps,
        loginHintSupported: rpServiceConfiguration.loginHintSupported,
        permissions: rpServiceConfiguration.permissions,
        policies: formattedPolicies,
        policyConfig: formattedPolicyConfig,
        properties: properties,
        redirects: rpServiceConfiguration.redirects,
        relyingParty: relyingParty,
        scoreAttributeTypeIDs: rpServiceConfiguration.scoreAttributeTypeIDs,
        scoreServiceOfferingsIDs: rpServiceConfiguration.scoreServiceOfferingsIDs,
        serviceName: rpServiceConfiguration.serviceName,
        serviceOfferings: formattedServiceOfferings,
        serviceType: rpServiceConfiguration.serviceType || OIDC_CREATEACCOUNT_SERVICE_TYPE,
        sharedSecret: rpServiceConfiguration.sharedSecret,
        status: rpServiceConfiguration.status,
        trustScoreModifiers: formattedTrustScoreModifiers,
        usePolicyManager: rpServiceConfiguration.usePolicyManager,
        workflowID: rpServiceConfiguration.workflowID,
        workflow: rpServiceConfiguration.workflow,
        organization
    };
};

export const formatRpServicePropertiesFromIdwProfileProperties = (
    properties: Array<FormattedIdwProfileProperty>
): Array<RpServiceConfigurationProperty> => {
    return properties.map(property => {
        const newProperty: RpServiceConfigurationProperty = {
            id: property.id,
            propertyID: property.availableProperty.id,
            rpServiceID: null,
            value: property.value
        };
        return newProperty;
    });
};


export const unformatRpServiceConfigurations = (verificationServices: Array<FormattedRpServiceConfiguration>, availableLanguages: Array<LocaleTypes>) => {
    return verificationServices.map(verificationServiceDetails => {
        return unformatRpServiceConfiguration(verificationServiceDetails, availableLanguages);
    });

}

export const unformatRpServiceConfiguration = (verificationServiceDetails: FormattedRpServiceConfiguration,
    availableLanguages: Array<LocaleTypes>) => {
    const localeTypeValues = Object.values(LocaleTypes);

    const attributes: Array<RpServiceConfigurationAttribute> = verificationServiceDetails.attributes
        .filter(attr => attr.addedToService)
        .map(attr => ({
            attributeLabelID: attr.attributeLabel.id,
            attributeTypeID: attr.attributeType.id,
            autoSubmitFlag: attr.autoSubmitFlag,
            customError: attr.customError,
            customPINContactURL: attr.customPINContactURL,
            customTemplateName: attr.customTemplateName,
            customToolTip: attr.customToolTip,
            dependentAttributeID: attr.dependentAttributeID,
            doNotSaveToPDS: attr.doNotSaveToPDS,
            id: attr.id,
            ignorePDSData: attr.ignorePDSData,
            isAsync: attr.isAsync,
            optionalFlag: attr.optionalFlag,
            order: `${attr.order}`,
            placeholderFlag: attr.placeholderFlag,
            requiredForSelfServiceVerification: attr.requiredForSelfServiceVerification,
            rpServiceID: attr.rpServiceID,
            status: attr.status,
            userEditableFlag: attr.userEditableFlag,
            userVisibleFlag: attr.userVisibleFlag,
            populateCredentialFromThisAttribute: attr.populateCredentialFromThisAttribute,
            displayFullValue: attr.displayFullValue
        }));

    const idps: Array<RpServiceConfigurationIdentityProvider> = verificationServiceDetails.idps
        .filter(idp => idp.addedToService)
        .map(idp => ({
            id: idp.id,
            identityProviderID: idp.identityProvider?.id,
            rpServiceID: idp.id
        }));

    const getDefaultAction = (policyConfig: FormattedRpServiceConfigurationPolicyConfig) => {
        let defaultAction: string = policyConfig.defaultAction;
        if (policyConfig.defaultAction && policyConfig.defaultAction === PolicyDecisionActionTypes.OBLIGATION && policyConfig.defaultActionStepUpApiKey) {
            defaultAction = `${policyConfig.defaultAction}:${policyConfig.defaultActionStepUpApiKey}`;
        }
        return defaultAction;
    };

    const policyConfig: RpServiceConfigurationPolicyConfig = verificationServiceDetails.policyConfig ? {
        decisions: (verificationServiceDetails.policyConfig.decisions || []).map(decision => ({
            conditions: (decision.conditions || []).map(condition => ({
                assertionID: condition.assertionID || null,
                attributeFieldID: condition.attributeField?.id || null,
                componentID: condition.component?.id || null,
                description: condition.description,
                id: condition.id,
                policyConditionOperator: condition.policyConditionOperator === PolicyConfigDecisionAttributeFieldResultTypes.MATCHES
                    ? PolicyConfigDecisionAttributeFieldResultTypes.EQUAL_TO
                    : condition.policyConditionOperator,
                result: condition.result,
                serviceOfferingID: condition.serviceOfferingID,
                values: condition.values
            }) as RpServiceConfigurationPolicyConfigDecisionCondition),
            description: decision.description,
            disposition: decision.disposition,
            id: decision.id,
            obligationValue: decision.obligationValue,
            policyDecisionOperationType: decision.policyDecisionOperationType,
            policyDecisionType: decision.policyDecisionType,
            retries: decision.retries,
            status: decision.status,
            uuid: decision.uuid
        }) as RpServiceConfigurationPolicyConfigDecision),
        defaultAction: getDefaultAction(verificationServiceDetails.policyConfig),
        defaultActionRetries: verificationServiceDetails.policyConfig.defaultActionRetries,
        description: verificationServiceDetails.policyConfig.description,
        id: verificationServiceDetails.policyConfig.id,
        name: verificationServiceDetails.serviceName,
        policyRetries: verificationServiceDetails.policyConfig.policyRetries,
        rpAXNServiceID: verificationServiceDetails.policyConfig.rpAXNServiceID,
        status: verificationServiceDetails.policyConfig.status
    } : null;

    const properties: Array<RpServiceConfigurationProperty> = verificationServiceDetails.properties
        .filter(property => property.addedToService)
        .map(property => {
            let value = property.value;
            if (property.propertyTypeId === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
                try {
                    const parsedJSON = property.value ? JSON.parse(property.value) : {};
                    if (Object.keys(parsedJSON).every(key => localeTypeValues.includes(key as LocaleTypes))) {
                        const passedThroughValues: MultiLanguageWrapper<AXNUIContent> = getPropertyValuesPassedToServiceWithDefault(
                            verificationServiceDetails.appliedProfiles,
                            DEFAULT_AXNUI_CONTENT,
                            property.propertyTypeId
                        );
                        value = performUnmergeForForMultiLanguageContentOuter(passedThroughValues, property.value, availableLanguages);
                    } else {
                        const passedThroughValues: AXNUIContent = getPropertyValuesPassedToServiceWithDefault(
                            verificationServiceDetails.appliedProfiles,
                            DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH],
                            property.propertyTypeId
                        );
                        value = performUnmergeForContentOuter(passedThroughValues, property.value);
                    }
                } catch (error) {
                    console.log(`error parsing JSON for AXNUI Content property value: ${property.value}, error: ${error}`);
                }
            } else if (property.propertyTypeId === AXNUI_LAYOUT_PROPERTY_TYPE_ID) {
                const passedThroughValues = getPropertyValuesPassedToServiceWithDefault(verificationServiceDetails.appliedProfiles, DEFAULT_AXNUI_LAYOUT, property.propertyTypeId);
                value = performUnmergeForContentOuter(passedThroughValues, property.value);
            } else if (property.propertyTypeId === AXNUI_THEME_PROPERTY_TYPE_ID) {
                const passedThroughValues = getPropertyValuesPassedToServiceWithDefault(verificationServiceDetails.appliedProfiles, DEFAULT_AXNUI_THEME, property.propertyTypeId);
                value = performUnmergeForContentOuter(passedThroughValues, property.value);
            } else if (property.propertyTypeId === DCUI_CONTENT_PROPERTY_TYPE_ID) {
                try {
                    const parsedJSON = property.value ? JSON.parse(property.value) : {};
                    if (Object.keys(parsedJSON).every(key => localeTypeValues.includes(key as LocaleTypes))) {
                        const passedThroughValues: MultiLanguageWrapper<DCUIContent> = getPropertyValuesPassedToServiceWithDefault(
                            verificationServiceDetails.appliedProfiles,
                            DEFAULT_DCUI_CONTENT,
                            property.propertyTypeId
                        );

                        value = performUnmergeForForMultiLanguageContentOuter(passedThroughValues, property.value, availableLanguages);
                    } else {
                        const passedThroughValues: DCUIContent = getPropertyValuesPassedToServiceWithDefault(
                            verificationServiceDetails.appliedProfiles,
                            DEFAULT_DCUI_CONTENT[LocaleTypes.ENGLISH],
                            property.propertyTypeId
                        );
                        value = performUnmergeForContentOuter(passedThroughValues, property.value);
                    }
                } catch (error) {
                    console.log(`error parsing JSON for DCUI Content property value: ${property.value}. Error: ${error}`);
                }
            } else if (property.propertyTypeId === DCUI_THEME_PROPERTY_TYPE_ID) {
                const passedThroughValues = getPropertyValuesPassedToServiceWithDefault(verificationServiceDetails.appliedProfiles, DEFAULT_DCUI_THEME, property.propertyTypeId);
                value = performUnmergeForContentOuter(passedThroughValues, property.value);
            } else if (property.propertyTypeId === EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID) {
                const passedThroughValues = getPropertyValuesPassedToServiceWithDefault(verificationServiceDetails.appliedProfiles, {} as EmailTemplateFields, property.propertyTypeId);
                value = performUnmergeForContentOuter(passedThroughValues, property.value);
            } else {
                const passedThroughValue = getPropertyValuesPassedToService(verificationServiceDetails.appliedProfiles, property.propertyTypeId);
                if (property.value && passedThroughValue === property.value) {
                    return null;
                }
            }

            return {
                id: property.propertyId,
                propertyID: property.propertyTypeId,
                rpServiceID: verificationServiceDetails.id,
                value: value
            };
        }).filter(x => x);

    const finalServiceOfferings: Array<RpServiceConfigurationServiceOffering> = verificationServiceDetails.serviceOfferings
        .filter(so => so.addedToService)
        .map(so => ({
            assertions: so.assertions.filter(assertion => assertion.addedToService).map(assertion => ({
                apAssertionID: assertion.apAssertionId,
                assertionMessageID: assertion.assertionMessageID,
                closeAssertionMessageID: assertion.closeAssertionMessageID,
                failAssertionMessageID: assertion.failAssertionMessageID,
                id: assertion.id,
                partialAssertionMessageID: assertion.partialAssertionMessageID,
                passAssertionMessageID: assertion.passAssertionMessageID,
                serviceOfferingID: assertion.serviceOfferingID,
                status: assertion.status,
                unverifiedAssertionMessageID: assertion.unverifiedAssertionMessageID,
                warnAssertionMessageID: assertion.warnAssertionMessageID,
            }) as RpServiceConfigurationServiceOfferingAssertion),
            category: so.category,
            dataSource: so.dataSource,
            fallbackRPServiceSOID: so.fallbackRPServiceSOID,
            fallbackServiceOfferingID: so.fallbackServiceOfferingID,
            fallbackStatus: so.fallbackStatus,
            id: so.id,
            order: `${so.order}`,
            processingType: so.processingType,
            processingTypeID: so.processingTypeID,
            referenceCode: so.referenceCode,
            refreshRate: so.refreshRate,
            rpServiceID: so.rpServiceID,
            serviceOfferingID: so.serviceOfferingID,
            status: so.status,
            weight: so.weight
        }));

    const serviceType = verificationServiceDetails.serviceType;

    const appliedProfiles: Array<AppliedProfile> = verificationServiceDetails.appliedProfiles.map(appliedProfile => {
        const profileProperties: Array<IdwProfileProperty> = appliedProfile.profile.profileProperties
            .filter(pp => pp.addedToProfile)
            .map(profileProperty => {
                return {
                    availablePropertyID: profileProperty.availableProperty?.id,
                    id: profileProperty.id,
                    value: profileProperty.value
                };
            });

        const profile: IdwProfile = {
            companyID: appliedProfile.profile.company?.id || null,
            id: appliedProfile.profile.id,
            name: appliedProfile.profile.name,
            profileProperties: profileProperties
        };
        return {
            id: appliedProfile.id,
            nickname: appliedProfile.nickname,
            precedence: appliedProfile.precedence.toFixed(1),
            profile: profile,
            referrerID: appliedProfile.referrerID,
            triggerProfileID: appliedProfile.triggerProfileID,
            type: appliedProfile.type
        }
    });

    return {
        afterIDPAuthURL: verificationServiceDetails.afterIDPAuthURL,
        apiKey: verificationServiceDetails.apiKey,
        appliedProfiles: appliedProfiles,
        assertionScoreModifiers: getAssertionScoreModifiers(verificationServiceDetails.assertionScoreModifiers),
        attributes: attributes,
        callBackURL: verificationServiceDetails.callBackURL,
        customIDPDisplayURL: verificationServiceDetails.customIDPDisplayURL,
        endpointTypeID: verificationServiceDetails.endpointTypeID,
        errorURL: verificationServiceDetails.errorURL,
        generalSettings: verificationServiceDetails.generalSettings,
        id: verificationServiceDetails.id,
        idps: idps,
        loginHintSupported: verificationServiceDetails.loginHintSupported,
        permissions: verificationServiceDetails.permissions,
        policies: verificationServiceDetails.policies,
        policyConfig: policyConfig,
        properties: properties,
        redirects: verificationServiceDetails.redirects,
        relyingPartyID: verificationServiceDetails.relyingParty?.id,
        scoreAttributeTypeIDs: verificationServiceDetails.scoreAttributeTypeIDs,
        scoreServiceOfferingsIDs: verificationServiceDetails.scoreServiceOfferingsIDs,
        serviceName: verificationServiceDetails.serviceName,
        serviceOfferings: finalServiceOfferings,
        serviceType: serviceType,
        sharedSecret: verificationServiceDetails.sharedSecret,
        status: verificationServiceDetails.status,
        trustScoreModifiers: verificationServiceDetails.trustScoreModifiers,
        usePolicyManager: verificationServiceDetails.usePolicyManager,
        workflowID: verificationServiceDetails.workflowID,
        workflow: verificationServiceDetails.workflow
    };
}